import { useNavigate } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Compass, Layers, Loader2, Pencil, Plus, Tag, Users, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import { SiteGroupsApi, type SiteGroup, type SiteGroupInput } from '@/services/sites/siteGroupsApi'
import { SitesApi } from '@/services/sites/sitesApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { AppShell } from '../../components/AppShell'
import { DetailList, DetailRow } from '../../components/DetailList'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { LabelAutocomplete } from '../../components/LabelAutocomplete'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useEditMode } from '../../hooks/useEditMode'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { formatDate } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

// ── Inner form component ────────────────────────────────────────────────────

interface SiteGroupEditFormProps {
  siteGroup: SiteGroup | null
  id: string
  isNew: boolean
}

function SiteGroupEditForm({ siteGroup, id, isNew }: SiteGroupEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()
  const { isEditing, startEditing, stopEditing } = useEditMode(isNew)

  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
  })

  // Which sites are in the group is held on the sites themselves, so the
  // members are looked up rather than edited here.
  const { data: members = [] } = useQuery({
    queryKey: ['site-group-sites', id],
    queryFn: () => SitesApi.getBySiteGroup(id),
    enabled: !isNew && !!id,
  })

  const form = useForm({
    defaultValues: {
      name: siteGroup?.name ?? '',
      description: siteGroup?.description ?? '',
      ownerTeamId: siteGroup?.ownerTeamId ?? '',
      labels: siteGroup?.labels ?? [],
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        description: value.description,
        ownerTeamId: value.ownerTeamId || undefined,
        labels: value.labels,
        objectState: (siteGroup?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => SiteGroupsApi.update(id, { name: siteGroup?.name ?? '', objectState }),
    ['site-group', id],
    ['site-groups']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: SiteGroupInput) =>
      isNew ? SiteGroupsApi.create(id, input) : SiteGroupsApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['site-group', id], data)
      queryClient.invalidateQueries({ queryKey: ['site-groups'] })
      markClean()
      if (isNew) navigate(`/publishing/site-groups/${id}`, { replace: !createDialog })
      else stopEditing()
      toast.success(t('common.saved'), {
        description: isNew ? t('siteGroups.createdSuccess') : t('siteGroups.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('siteGroups.createFailed')
            : t('siteGroups.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('siteGroups.title'), href: '/publishing/site-groups' },
    { label: isNew ? t('siteGroups.newSiteGroup') : (siteGroup?.name ?? t('siteGroups.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={siteGroup?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.siteGroup')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('siteGroups.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {!isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
                <ObjectStateToggle value={siteGroup!.objectState} onChange={saveObjectState} />
              {!isEditing && (
                <Button type="button" variant="outline" onClick={startEditing}>
                  <Pencil className="size-3.5" />
                  {t('common.edit')}
                </Button>
              )}
            </div>
          )}
        </div>

        {siteGroup && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={siteGroup.id} /> },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(siteGroup.createTime)}
                    updated={formatDate(siteGroup.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="flex flex-col gap-3.5 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-3.5">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Layers} />
                  {t('siteGroups.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="field-grid">
                    <div className="p-field full">
                      <FieldLabel tooltip={t('siteGroups.descriptionHelp')}>
                        {t('common.description')}
                      </FieldLabel>
                      <form.Field name="description">
                        {(field) => (
                          <Textarea
                            rows={3}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('siteGroups.descriptionPlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <FieldLabel tooltip={t('siteGroups.ownerTeamHelp')}>
                        {t('common.team')}
                      </FieldLabel>
                      <form.Field name="ownerTeamId">
                        {(field) => (
                          <Select
                            label={t('common.team')}
                            value={field.state.value}
                            onValueChange={(v) =>
                              field.handleChange(v === '__clear__' ? '' : (v ?? ''))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t('siteGroups.selectTeam')}>
                                {teams.find((o) => o.id === field.state.value)?.name ?? null}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__clear__">—</SelectItem>
                              {teams.map((team) => (
                                <SelectItem key={team.id} value={team.id}>
                                  {team.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <FieldLabel>{t('common.labels')}</FieldLabel>
                      <form.Field name="labels">
                        {(field) => (
                          <LabelAutocomplete
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                ) : (
                  <DetailList>
                    <DetailRow label={t('common.description')}>
                      {siteGroup?.description}
                    </DetailRow>
                    <DetailRow label={t('common.team')} icon={Users}>
                      {siteGroup?.ownerTeamName}
                    </DetailRow>
                    <DetailRow label={t('common.labels')} icon={Tag}>
                      {siteGroup?.labels.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {siteGroup.labels.map((label) => (
                            <span
                              key={label}
                              className="rounded-full border border-(--ad-badge-blue-border) bg-(--ad-badge-blue-bg) px-2 py-0.5 text-xs font-medium text-(--ad-badge-blue)"
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </DetailRow>
                  </DetailList>
                )}
              </CardContent>
            </Card>

            {!isNew && (
              <Card tone="delivery">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Compass} />
                    {t('siteGroups.sitesInGroup', { count: members.length })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {members.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('siteGroups.noSites')}</p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {members.map((site) => (
                        <li key={site.id}>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0"
                            onClick={() => navigate(`/publishing/sites/${site.id}`)}
                          >
                            {site.name}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-3 text-xs text-muted-foreground">
                    {t('siteGroups.membershipHelp')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <div
          className={`detail-savebar${isEditing || isDirty || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => (isNew ? cancelNew('/publishing/site-groups') : stopEditing())}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('siteGroups.newSiteGroup') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function SiteGroupEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: siteGroup,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['site-group', id],
    queryFn: () => SiteGroupsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('siteGroups.title'), href: '/publishing/site-groups' },
    { label: isNew ? t('siteGroups.newSiteGroup') : t('siteGroups.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return <SiteGroupEditForm key={id} siteGroup={siteGroup ?? null} id={id!} isNew={isNew} />
}
